/**
 * PATH: apps/quote/src/pages/templates-page/TemplateCard.tsx
 * PURPOSE: Grid-view template card with actions menu
 */

import { useState } from 'react'
import { FileSpreadsheet, MoreVertical, Edit, Copy, Archive, Sparkles } from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'
import type { SpreadsheetTemplate, TemplateStatus } from '@/lib/quoteApi'

const STATUS_COLORS: Record<TemplateStatus, { bg: string; text: string }> = {
  draft: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  archived: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400' },
}

interface TemplateCardProps {
  template: SpreadsheetTemplate
  onUseTemplate: (template: SpreadsheetTemplate) => void
  onEdit: (template: SpreadsheetTemplate) => void
  onDuplicate: (template: SpreadsheetTemplate) => void
  onArchive: (template: SpreadsheetTemplate) => void
}

export default function TemplateCard({ template, onUseTemplate, onEdit, onDuplicate, onArchive }: TemplateCardProps) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="group bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <FileSpreadsheet className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white truncate max-w-[180px]">{template.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', STATUS_COLORS[template.status].bg, STATUS_COLORS[template.status].text)}>{template.status}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">{template.category}</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreVertical className="h-4 w-4 text-slate-500" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1">
                <button onClick={() => { onEdit(template); setShowMenu(false) }} className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"><Edit className="h-4 w-4" /> Edit</button>
                <button onClick={() => { onDuplicate(template); setShowMenu(false) }} className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"><Copy className="h-4 w-4" /> Duplicate</button>
                <button onClick={() => { onArchive(template); setShowMenu(false) }} className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2"><Archive className="h-4 w-4" /> Archive</button>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4 min-h-[40px]">{template.description || 'No description provided'}</p>

      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg px-3 py-2">
          <span className="text-slate-500 dark:text-slate-400 block text-xs">Bindings</span>
          <span className="font-semibold text-slate-900 dark:text-white">{template.binding_count}</span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg px-3 py-2">
          <span className="text-slate-500 dark:text-slate-400 block text-xs">Used</span>
          <span className="font-semibold text-slate-900 dark:text-white">{template.times_used}x</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700">
        <span className="text-xs text-slate-500 dark:text-slate-400">v{template.version} · {template.updated_at ? formatDate(template.updated_at) : 'Never updated'}</span>
        <button onClick={() => onUseTemplate(template)} disabled={template.status !== 'active'} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors', template.status === 'active' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed')}>
          <Sparkles className="h-3.5 w-3.5" /> Use
        </button>
      </div>
    </div>
  )
}
